<template>
  <!-- Requisitos para ejecutarse: una pastilla en la barra («Lista» / «4/6 · faltan 2») que despliega
       la lista con su casilla; un requisito pendiente lleva a donde se arregla. -->
  <div ref="rootRef" class="rc">
    <button type="button" class="rc-pill" :class="ready ? 'ok' : 'todo'" :aria-expanded="open" @click="open = !open">
      <span class="rc-ring" aria-hidden="true">
        <svg viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" class="rc-track" />
          <circle cx="10" cy="10" r="8" class="rc-done" :stroke-dasharray="`${50.27 * done / total} 50.27`" /></svg>
        <span v-if="ready" class="rc-tick">✓</span>
      </span>
      {{ ready ? t('chk.ready') : t('chk.missing', { done, total, n: total - done }) }}
      <span v-if="ready && noteCount" class="rc-badge" :class="{ danger: noteDanger }" :aria-label="t('adv.count', { n: noteCount })">{{ noteCount }}</span>
    </button>
    <div v-if="open" class="rc-pop" role="dialog" :aria-label="t('chk.title')">
      <div class="rc-head">{{ t('chk.title') }}</div>
      <ul class="rc-list">
        <li v-for="it in items" :key="it.key" :class="{ ok: it.ok }">
          <button type="button" class="rc-item" :disabled="it.ok || !it.target" @click="go(it.target)">
            <span class="rc-box" aria-hidden="true">{{ it.ok ? '✓' : '' }}</span>
            <span class="rc-txt">
              {{ t(it.label) }}
              <span v-if="!it.ok && it.missing && it.missing.length" class="rc-miss">{{ t('chk.pendingIn', { forms: it.missing.join(' · ') }) }}</span>
            </span>
            <span v-if="!it.ok && it.target" class="rc-go" aria-hidden="true">→</span>
          </button>
        </li>
      </ul>
      <template v-if="notes.length">
        <div class="rc-head note">{{ t('adv.title') }}</div>
        <ul class="rc-list">
          <li v-for="nt in notes" :key="nt.key" :class="'adv ' + nt.level">
            <button type="button" class="rc-item" :disabled="!nt.target" @click="go(nt.target)">
              <span class="rc-ico" aria-hidden="true">{{ nt.level === 'danger' ? '■' : '▲' }}</span>
              <span class="rc-txt">
                {{ t(nt.label) }}
                <span v-if="nt.missing.length" class="rc-miss">{{ t('adv.in', { list: nt.missing.join(' · ') }) }}</span>
              </span>
              <span v-if="nt.target" class="rc-go" aria-hidden="true">→</span>
            </button>
          </li>
        </ul>
      </template>
      <template v-if="server.length">
        <div class="rc-head warn">{{ t('chk.server') }}</div>
        <ul class="rc-list">
          <li v-for="(p, i) in server" :key="i"><span class="rc-item"><span class="rc-box" aria-hidden="true" /><span class="rc-txt">{{ p }}</span></span></li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';

const props = defineProps({
  items: { type: Array, required: true },
  /** Problemas que devolvió el servidor al guardar (además de los de la lista). */
  server: { type: Array, default: () => [] },
  /** Avisos que no impiden ejecutar ([{key, level, label, missing, target}]). */
  notes: { type: Array, default: () => [] },
});
const emit = defineEmits(['go']);

const open = ref(false);
const rootRef = ref(null);
const total = computed(() => props.items.length + props.server.length);
const done = computed(() => props.items.filter((i) => i.ok).length);
const ready = computed(() => done.value === total.value);
const noteCount = computed(() => props.notes.reduce((n, x) => n + Math.max(1, x.missing.length), 0));
const noteDanger = computed(() => props.notes.some((x) => x.level === 'danger'));

function go(target) {
  open.value = false;
  emit('go', target);
}

function onDocDown(e) {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target)) open.value = false;
}

function onKey(e) {
  if (e.key === 'Escape') open.value = false;
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocDown);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown);
  document.removeEventListener('keydown', onKey);
});

defineExpose({ show: () => { open.value = true; }, hide: () => { open.value = false; } });
</script>

<style>
.vem-root .rc { position: relative; }
.vem-root .rc-pill {
  display: inline-flex; align-items: center; gap: 7px; min-height: 34px; padding: 4px 12px 4px 7px; border-radius: 100px;
  border: 1px solid var(--line2); background: var(--ink); color: var(--tx); cursor: pointer; font-weight: 600; font-size: 12.5px;
}
.vem-root .rc-pill.ok { color: var(--mint); border-color: var(--mint-bd); background: var(--mint-bg); }
.vem-root .rc-pill.todo { color: var(--amber); border-color: var(--amber-bd); }
.vem-root .rc-ring { position: relative; width: 20px; height: 20px; display: grid; place-items: center; }
.vem-root .rc-ring svg { position: absolute; inset: 0; transform: rotate(-90deg); }
.vem-root .rc-track { fill: none; stroke: var(--mute-bg); stroke-width: 2.6; }
.vem-root .rc-done { fill: none; stroke: currentColor; stroke-width: 2.6; stroke-linecap: round; transition: stroke-dasharray .35s ease; }
.vem-root .rc-tick { position: relative; font-size: 11px; }
.vem-root .rc-pop {
  position: absolute; z-index: 20; top: calc(100% + 8px); right: 0; width: 340px; background: var(--panel);
  border: 1px solid var(--line2); border-radius: 12px; padding: 10px; box-shadow: 0 14px 36px var(--shadow);
}
.vem-root .rc-head { font-family: var(--mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3); margin: 2px 4px 6px; }
.vem-root .rc-head.warn { color: var(--amber); margin-top: 10px; }
.vem-root .rc-list { list-style: none; margin: 0; padding: 0; }
.vem-root .rc-item {
  display: flex; align-items: flex-start; gap: 9px; width: 100%; text-align: left; background: none; border: 0; border-radius: 8px;
  padding: 6px; color: var(--tx); cursor: pointer; font-size: 12.5px; line-height: 1.35;
}
.vem-root .rc-item:hover:not(:disabled) { background: var(--hover); }
.vem-root .rc-item:disabled { cursor: default; }
.vem-root li.ok .rc-item { color: var(--tx2); }
.vem-root .rc-box {
  flex: 0 0 16px; height: 16px; margin-top: 1px; border-radius: 4px; border: 1.5px solid var(--amber-bd);
  display: grid; place-items: center; font-size: 11px; font-weight: 700; color: #fff;
}
.vem-root li.ok .rc-box { background: var(--mint); border-color: var(--mint); }
.vem-root .rc-txt { flex: 1; min-width: 0; }
.vem-root .rc-miss { display: block; font-size: 11.5px; color: var(--amber); overflow-wrap: anywhere; }
.vem-root .rc-go { color: var(--tx3); }
.vem-root .rc-pill { position: relative; }
.vem-root .rc-badge {
  position: absolute; top: -6px; right: -6px; min-width: 17px; height: 17px; padding: 0 4px; border-radius: 9px;
  background: var(--amber); color: #fff; font-size: 10px; font-weight: 700; display: grid; place-items: center;
  box-shadow: 0 0 0 2px var(--panel);
}
.vem-root .rc-badge.danger { background: var(--danger); }
.vem-root .rc-head.note { color: var(--amber); margin-top: 10px; }
.vem-root .rc-ico { flex: 0 0 16px; text-align: center; font-size: 11px; color: var(--amber); }
.vem-root li.adv.danger .rc-ico { color: var(--danger); }
</style>
